import { loadCachedData } from '../../storage';
import { MAX_RETRIES, BASE_DELAY_MS } from './constants';
import { fetchExchangeRates } from './client';

interface FetchRatesOptions {
  maxRetries?: number;
  baseDelayMs?: number;
}

/**
 * Fetches exchange rates for baseCurrency with up to 3 retry attempts
 * using exponential backoff (1s, 2s, 4s). Checks sessionStorage cache first
 * (60-min TTL). Does NOT return stale data if all retries fail — throws instead.
 */
export async function fetchRatesWithRetry(
  baseCurrency: string,
  options?: FetchRatesOptions
): Promise<Record<string, number>> {
  const maxRetries = options?.maxRetries ?? MAX_RETRIES;
  const baseDelayMs = options?.baseDelayMs ?? BASE_DELAY_MS;

  // Check cache first — return if still valid (< 60 min old)
  const cached = loadCachedData<ExchangeRates>(baseCurrency);
  if (cached) {
    return cached.rates;
  }

  // Cache miss or expired — fetch from API with retries
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetchExchangeRates(baseCurrency);
      return response.rates;
    } catch {
      // Don't wait after the last attempt
      if (attempt < maxRetries - 1) {
        const delayMs = baseDelayMs * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }

  throw new Error('Exchange rate fetch failed after 3 retries');
}
